var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { datadogRum } from '@datadog/browser-rum';
import Service, { inject as service } from '@ember/service';
import environment from '@square/environment-utils/environment';

// https://prototype.sqprod.co/#/docs/squareup.multipass.PasskeyCredential

/**
 * Encode given buffer or decode given string with Base64URL.
 * Can also decode a given string (for example person token which is not base64) to base64
 */
const base64url = {
  encode: function (buffer) {
    const base64 = window.btoa(String.fromCharCode(...new Uint8Array(buffer)));
    return base64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  },
  encodeString: function (buffer) {
    const base64 = window.btoa(buffer);
    return base64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  },
  decode: function (base64url) {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const binStr = window.atob(base64);
    const bin = new Uint8Array(binStr.length);
    for (let i = 0; i < binStr.length; i++) {
      bin[i] = binStr.charCodeAt(i);
    }
    return bin.buffer;
  }
};
const preOptions = {
  authenticator_selection_criteria: {
    resident_key: 'required',
    requires_resident_key: true,
    user_verification: 'required'
  },
  attestation_conveyance_preference: 'none'
};
const SHOW_AGAIN_DAYS = 7;
const NEVER_SHOW_AGAIN_DAYS = 365 * 1000;
const AUTO_ENROLL_DAYS_UNTIL_NEXT_ENROLL_ATTEMPT = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
// To test locally, add to options options.rp.id (or possible options.rpId) = 'localhost';
let EmberMultipassPasskeysService = (_dec = service('ember-multipassfe-client'), _dec2 = service('ember-multipass-tracking'), _dec3 = service('ember-multipass-logging'), _dec4 = service('ember-multipass-storage'), _class = class EmberMultipassPasskeysService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "multipassfeClient", _descriptor, this);
    _initializerDefineProperty(this, "tracking", _descriptor2, this);
    _initializerDefineProperty(this, "logger", _descriptor3, this);
    _initializerDefineProperty(this, "storage", _descriptor4, this);
    _defineProperty(this, "abortController", null);
    _defineProperty(this, "verification_types", Object.freeze({
      NO_VERIFICATION: 'NO_VERIFICATION',
      PASSWORD: 'PASSWORD',
      TWO_FACTOR: 'TWO_FACTOR'
    }));
    /**
     * Create and register a new passkey. May throw multipass registerPasskey endpoint errors
     * @returns {Promise<boolean>} `true` if the passkey was successfully registered, `false` if the user needs to verify their identity.
     */
    _defineProperty(this, "registerCredential", async ({
      verificationHandler,
      password,
      shouldAttemptConditional = false,
      location
    }) => {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const environment = this.multipassfeClient.getEnvironment();

      // Check if conditional create is supported when requested
      let useConditional = false;
      if (shouldAttemptConditional) {
        // @ts-ignore getClientCapabilities is not in ts dom definitions
        if (window.PublicKeyCredential && PublicKeyCredential.getClientCapabilities) {
          // @ts-ignore getClientCapabilities is not in ts dom definitions
          const capabilities = await PublicKeyCredential.getClientCapabilities();
          useConditional = !!capabilities['conditionalCreate'];
        }
      }
      const initialOptions = structuredClone(preOptions);
      if (password) {
        initialOptions.password = password;
      }
      if (useConditional) {
        initialOptions.authenticator_selection_criteria.user_verification = 'preferred';
      }
      const {
        registration_id,
        public_key_credential_options,
        verification
      } = await this.multipassfeClient.registerPasskey(initialOptions);
      if (verification === this.verification_types.TWO_FACTOR || verification === this.verification_types.PASSWORD) {
        datadogRum?.addAction('passkey_register_verification_required', {
          verification,
          location,
          environment
        });
        verificationHandler?.(verification);
        return false;
      }
      const options = JSON.parse(public_key_credential_options);
      if (!options || !registration_id) {
        return false;
      }

      // Base64URL decode the server-provided user.id
      options.user.id = base64url.decode(options.user.id);
      options.challenge = base64url.decode(options.challenge);
      options.attestationType = 'none';
      options.user.displayName = options.user.name;
      if (options.excludeCredentials) {
        for (const cred of options.excludeCredentials) {
          cred.id = base64url.decode(cred.id);
        }
      }
      const mediation = useConditional ? 'conditional' : undefined;
      const createCredential = async useMediation => {
        const cred = await navigator.credentials.create({
          publicKey: options,
          signal: this.abortController?.signal,
          ...(useMediation && mediation && {
            mediation
          })
        });
        if (!cred) {
          throw new DOMException('No credential created', 'NotAllowedError');
        }
        return cred;
      };
      const logError = (event, err) => {
        const error = err?.name || err;
        datadogRum?.addAction(event, {
          location,
          environment,
          ...(Boolean(error) && {
            err: error
          })
        });
      };
      let cred;
      try {
        datadogRum?.addAction('passkey_register_open_webauthn_modal', {
          location,
          environment
        });
        cred = await createCredential(true);
      } catch (err) {
        const isConditionalBrowserFailure = err instanceof DOMException && err.name === 'NotAllowedError' && mediation === 'conditional';
        if (isConditionalBrowserFailure) {
          // Check if we should attempt auto-enroll before retrying (for both login and password reset flows)
          const isAutoEnrollLocation = location === 'LOGIN_AUTO_ENROLL' || location === 'PASSWORD_RESET';
          const shouldAttemptAutoEnroll = isAutoEnrollLocation && this.shouldAttemptPasskeyAutoEnroll();
          if (shouldAttemptAutoEnroll) {
            logError('passkey_register_retry_as_non_conditional');
            try {
              cred = await createCredential(false);
            } catch (retryErr) {
              logError('passkey_register_non_conditional_failed', err);
              // Record auto-enroll attempt since retry failed
              this.recordAutoEnrollAttempt();
              return false;
            }
          } else {
            logError('passkey_register_attempt_check_false');
            return false;
          }
        } else {
          if (this.isAcceptableError(err)) {
            logError('passkey_register_user_cancelled', err);
          } else {
            logError('passkey_register_webauthn_error', err);
          }
          return false;
        }
      }
      const credential = {};
      credential.id = cred.id;
      credential.rawId = base64url.encode(cred.rawId);
      credential.type = cred.type;
      const clientDataJSON = base64url.encode(cred.response.clientDataJSON);
      const attestationObject = base64url.encode(cred.response.attestationObject);
      const transports = cred.response.getTransports ? cred.response.getTransports() : [];
      credential.response = {
        clientDataJSON,
        attestationObject,
        transports
      };
      let response = {
        public_key_credential: JSON.stringify(credential),
        registration_id
      };
      if (password) {
        response = {
          ...response,
          password: password
        };
      }
      try {
        await this.multipassfeClient.registerPasskey(response, {
          createToken: true
        });
        datadogRum?.addAction('passkey_register_success', {
          location,
          environment
        });
      } catch (err) {
        // @ts-expect-error exists in javascript but not in typescript yet
        if (PublicKeyCredential.signalUnknownCredential) {
          // @ts-expect-error exists in javascript but not in typescript yet
          await PublicKeyCredential.signalUnknownCredential({
            rpId: options.rp.id,
            credentialId: credential.id
          });
        }
        throw err;
      }
      return true;
    });
    _defineProperty(this, "getRegisterCredentialVerificationType", async () => {
      const {
        verification
      } = await this.multipassfeClient.registerPasskey(preOptions);
      return verification;
    });
    /**
     * Request to deregister a passkey.
     * @param { string } credId A Base64URL encoded credential ID of the passkey to unregister.
     * @returns a promise that states whether the operation was successful or not
     */
    _defineProperty(this, "deregisterPasskey", async ({
      credId,
      verificationHandler,
      password
    }) => {
      const data = {};
      if (credId) data.credential_id = credId;
      if (password) data.password = password;
      const {
        verification
      } = await this.multipassfeClient.deregisterPasskey(data);
      if (verification === this.verification_types.TWO_FACTOR || verification === this.verification_types.PASSWORD) {
        verificationHandler(verification);
        return false;
      }
      datadogRum?.addAction('passkey_remove_credential', {
        credentialId: credId
      });
      this.attemptSignalAcceptedCredentials([credId]);
      return true;
    });
    /**
     * Request to get the verification status of a passkey deregistration.
     * @returns a promise that resolves with the verification status.
     */
    _defineProperty(this, "getDeregisterPasskeyVerificationStatus", async () => {
      const {
        verification
      } = await this.multipassfeClient.deregisterPasskey();
      return verification;
    });
    /**
     * Gets information from local storage regarding whether to show passkeys promo
     * @returns {boolean}
     */
    _defineProperty(this, "showPromoDontRemindAgain", () => {
      const dontRemindAgainDate = this.storage.getPasskeyPromoViewDontRemindDate();
      if (!dontRemindAgainDate) return true;
      return Date.now() >= new Date(dontRemindAgainDate).getTime();
    });
    /**
     * When user chooses to no longer be reminded, or to be reminded later, we set in local storage the date in which we will show again
     */
    _defineProperty(this, "setPromoDontRemindAgain", neverShowAgain => {
      const expiryDays = neverShowAgain ? NEVER_SHOW_AGAIN_DAYS : SHOW_AGAIN_DAYS;
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + expiryDays);
      this.storage.setPasskeyPromoViewDontRemindDate(expiryDate);
    });
    /**
     * Fully remove promo-view key from local storage
     */
    _defineProperty(this, "removePromoDontRemindAgain", () => {
      this.storage.removePasskeyPromoViewDontRemindDate();
    });
    /**
     * Check if we should attempt auto-enroll (pure check, no side effects).
     * Use recordAutoEnrollAttempt() after actually attempting enrollment.
     */
    _defineProperty(this, "shouldAttemptPasskeyAutoEnroll", () => {
      const numberOfAttempts = this.storage.getPasskeyAutoEnrollAttempts() ?? 0;
      const lastAttemptTimestamp = this.storage.getPasskeyAutoEnrollLastAttemptDate();
      if (numberOfAttempts >= 3) return false;

      // If there's a previous attempt, check if 30 days have passed
      if (lastAttemptTimestamp) {
        const daysSinceLastAttempt = (Date.now() - lastAttemptTimestamp) / MS_PER_DAY;
        // If less than 30 days since last attempt, don't try again
        if (daysSinceLastAttempt < AUTO_ENROLL_DAYS_UNTIL_NEXT_ENROLL_ATTEMPT) {
          return false;
        }
      }
      return true;
    });
    /**
     * Record that an auto-enroll attempt was made and declined. Call this after actually
     * attempting enrollment, not just when checking if we should attempt.
     */
    _defineProperty(this, "recordAutoEnrollAttempt", () => {
      const numberOfAttempts = this.storage.getPasskeyAutoEnrollAttempts() ?? 0;
      this.storage.setPasskeyAutoEnrollAttempts(numberOfAttempts + 1);
      this.storage.setPasskeyAutoEnrollLastAttemptDate(Date.now());
    });
    /**
     * Returns if the error is one that should be ignored due to user input (cancelled on their own)
     */
    _defineProperty(this, "isAcceptableError", err => {
      const KNOWN_ERRORS = ['NotAllowedError', 'AbortError', 'InvalidStateError'];
      return err instanceof DOMException && KNOWN_ERRORS.includes(err.name);
    });
    _defineProperty(this, "doesSupportPasskeys", () => {
      return Boolean(window.PublicKeyCredential);
    });
    _defineProperty(this, "doesSupportConditionalAuthentication", async () => {
      if (!this.doesSupportPasskeys()) return false;
      if (!PublicKeyCredential.isConditionalMediationAvailable) return false;
      return await PublicKeyCredential.isConditionalMediationAvailable();
    });
  }
  /**
   * Authenticate with a passkey.
   * @param { boolean } conditional Set to `true` if this is for a conditional UI.
   * @param { AuthenticateLocation } location The location where authentication is being triggered from.
   * @returns A promise that resolves with a server response.
   */
  async authenticate(conditional = false, location = 'SIGN_IN_WITH_PASSKEYS_BUTTON') {
    if (this.abortController) {
      this.abortController.abort('ABORTED_BY_SIGNAL');
      this.abortController = new AbortController();
    } else {
      this.abortController = new AbortController();
    }
    const environment = this.multipassfeClient.getEnvironment();
    if (!this.multipassfeClient.passkeyAuthPublicKeyCredentialRequestOptions) {
      return;
    }
    const options = JSON.parse(this.multipassfeClient.passkeyAuthPublicKeyCredentialRequestOptions);
    const authentication_id = options.challenge;
    options.challenge = base64url.decode(options.challenge);
    if (options.allowCredentials) {
      for (const allowCred of options.allowCredentials) {
        allowCred.id = base64url.decode(allowCred.id);
      }
    }
    datadogRum?.addAction('passkey_authenticate_initialize_webauthn_modal', {
      conditional,
      location,
      environment
    });
    let cred;
    try {
      cred = await navigator.credentials.get({
        publicKey: options,
        mediation: conditional ? 'conditional' : 'optional',
        signal: this.abortController.signal
      });
      if (!cred) {
        datadogRum?.addAction('passkey_authenticate_no_credential', {
          conditional,
          location,
          environment
        });
        return;
      }
    } catch (err) {
      if (this.isAcceptableError(err)) {
        datadogRum?.addAction('passkey_authenticate_user_cancelled', {
          conditional,
          location,
          err: err.name,
          environment
        });
      } else {
        datadogRum?.addAction('passkey_authenticate_webauthn_error', {
          conditional,
          location,
          err: err.name,
          environment
        });
      }
      return;
    }
    const credential = {};
    credential.id = cred?.id;
    credential.type = cred?.type;
    // Base64URL encode `rawId`
    credential.rawId = base64url.encode(cred?.rawId);
    // Base64URL encode some values
    const clientDataJSON = base64url.encode(cred.response?.clientDataJSON);
    const authenticatorData = base64url.encode(cred.response?.authenticatorData);
    const signature = base64url.encode(cred.response?.signature);
    const userHandle = base64url.encode(cred.response?.userHandle);
    credential.response = {
      clientDataJSON,
      authenticatorData,
      signature,
      userHandle
    };
    datadogRum?.addAction('passkey_authenticate_success', {
      conditional,
      location,
      environment
    });
    return {
      publicKeyCredential: JSON.stringify(credential),
      authenticationId: authentication_id
    };
  }

  /**
   * Request to update the name of a passkey.
   * @param { string } credentialId A Base64URL encoded credential ID of the passkey to update.
   * @param { string } name A new name for the passkey.
   * @returns a promise that resolves with a server response.
   */
  async updatePasskey(credentialId, name) {
    datadogRum?.addAction('passkey_update_name', {
      name
    });
    const response = await this.multipassfeClient.updatePasskey({
      credential_id: credentialId,
      name
    });
    this.attemptSignalNewUserInfo(name);
    return response;
  }
  async attemptSignalNewUserInfo(name) {
    if ('signalCurrentUserDetails' in PublicKeyCredential) {
      const {
        isProduction
      } = environment();
      const rpId = isProduction ? this.multipassfeClient.bareDomains['PRODUCTION'] : this.multipassfeClient.bareDomains['STAGING'];
      const userId = base64url.encodeString(this.multipassfeClient.personToken);
      const email = this.multipassfeClient.accountInformation?.email_address;
      // @ts-expect-error exists in javascript but not in typescript yet
      await PublicKeyCredential.signalCurrentUserDetails({
        rpId,
        userId,
        name: email,
        displayName: name
      });
      datadogRum?.addAction('passkey_update_signal_sent', {
        name
      });
      return true;
    }
    datadogRum?.addAction('passkey_update_signal_unavailable', {
      name
    });
    return false;
  }
  async attemptSignalAcceptedCredentials(removableIds = []) {
    if (!('signalAllAcceptedCredentials' in PublicKeyCredential)) {
      datadogRum?.addAction('passkey_signal_accepted_credentials_unavailable');
      return;
    }
    const {
      isProduction
    } = environment();
    const rpId = isProduction ? this.multipassfeClient.bareDomains['PRODUCTION'] : this.multipassfeClient.bareDomains['STAGING'];
    const userId = base64url.encodeString(this.multipassfeClient.personToken);
    const allAcceptedCredentialIds = this.multipassfeClient.passkeys?.reduce((listOfIds, currentPasskey) => {
      const credentialId = currentPasskey.credential_id;
      if (removableIds.includes(credentialId)) {
        return listOfIds;
      }
      return [...listOfIds, credentialId];
    }, []) ?? [];

    // @ts-expect-error exists in javascript but not in typescript yet
    await PublicKeyCredential.signalAllAcceptedCredentials({
      rpId,
      userId,
      allAcceptedCredentialIds
    });
    datadogRum?.addAction('passkey_signal_accepted_credentials_sent', {
      acceptedCredentialIds: allAcceptedCredentialIds,
      rpId,
      userId
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "multipassfeClient", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "tracking", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "logger", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "storage", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { EmberMultipassPasskeysService as default };